import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Eye,
  Building,
  Mail,
  Calendar,
  DollarSign,
  Target,
  User,
  Briefcase,
  CreditCard,
  Loader2,
  Download,
  Trash2,
} from "lucide-react";

const AdminApplications = () => {
  const { token, API } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deleteAppDialog, setDeleteAppDialog] = useState(null);
  const [deletingApp, setDeletingApp] = useState(false);
  const [clearDataDialog, setClearDataDialog] = useState(false);
  const [clearingData, setClearingData] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await fetch(`${API}/admin/applications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setApplications(data);
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (applicationId, action) => {
    setActionLoading(action);
    try {
      const params = new URLSearchParams({ action });
      const response = await fetch(
        `${API}/admin/applications/${applicationId}/action?${params.toString()}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchApplications();
        setDetailModalOpen(false);
        setSelectedApp(null);
        setConfirmAction(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to process action");
      }
    } catch (error) {
      toast.error("Failed to process action");
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const programPrices = {
    carerevive: "$750",
    foundations_first: "$1,250",
    frontline_concierge: "$2,200",
  };

  const programNames = {
    carerevive: "CareRevive Initiative",
    foundations_first: "Foundations First",
    frontline_concierge: "Frontline Concierge",
  };

  const filteredApplications = applications.filter((app) => {
    if (filter === "all") return true;
    if (filter === "pending_payment") return app.status === "pending_payment";
    if (filter === "approved") return app.status === "approved";
    if (filter === "denied") return app.status === "denied";
    return app.status === filter;
  });

  const getStatusBadge = (status, paymentStatus) => {
    const statusConfig = {
      approved: { variant: "default", icon: CheckCircle, label: "Approved", className: "bg-green-600" },
      denied: { variant: "destructive", icon: XCircle, label: "Denied" },
      pending_payment: { variant: "outline", icon: CreditCard, label: "Pending Payment", className: "border-yellow-500 text-yellow-600" },
      pending: { variant: "outline", icon: Clock, label: "Pending Review" },
      enrolled: { variant: "default", icon: CheckCircle, label: "Enrolled", className: "bg-green-600" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const openDetailModal = (app) => {
    setSelectedApp(app);
    setDetailModalOpen(true);
    setConfirmAction(null);
  };

  const renderActionButtons = (app) => {
    // Only show action buttons for pending_payment or pending status
    if (!["pending_payment", "pending"].includes(app.status)) {
      return null;
    }

    if (confirmAction) {
      const actionConfig = {
        approve: {
          title: "Confirm Approval",
          message: "This will create a client account and send the applicant their login credentials along with the program approval email.",
          buttonText: "Yes, Approve Application",
          buttonClass: "bg-green-600 hover:bg-green-700",
        },
        payment_not_received: {
          title: "Send Payment Reminder",
          message: "This will send the applicant an email reminding them to complete their payment with their submission ID.",
          buttonText: "Yes, Send Reminder",
          buttonClass: "bg-yellow-600 hover:bg-yellow-700",
        },
        deny: {
          title: "Confirm Denial",
          message: "This will send the applicant a denial email. This action cannot be undone.",
          buttonText: "Yes, Deny Application",
          buttonClass: "bg-red-600 hover:bg-red-700",
        },
      };

      const config = actionConfig[confirmAction];

      return (
        <div className="space-y-4 p-4 bg-slate-50 rounded-lg border">
          <h4 className="font-semibold text-rnb-navy">{config.title}</h4>
          <p className="text-sm text-rnb-gray">{config.message}</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmAction(null)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              className={config.buttonClass}
              onClick={() => handleAction(app.application_id, confirmAction)}
              disabled={actionLoading}
            >
              {actionLoading === confirmAction && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {config.buttonText}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          className="bg-green-600 hover:bg-green-700 text-white flex-1"
          onClick={() => setConfirmAction("approve")}
          data-testid="action-approve"
        >
          <CheckCircle className="h-4 w-4 mr-2" />
          Approve & Create Account
        </Button>
        <Button
          className="bg-yellow-600 hover:bg-yellow-700 text-white flex-1"
          onClick={() => setConfirmAction("payment_not_received")}
          data-testid="action-payment-pending"
        >
          <AlertTriangle className="h-4 w-4 mr-2" />
          Payment Not Received
        </Button>
        <Button
          variant="destructive"
          className="flex-1"
          onClick={() => setConfirmAction("deny")}
          data-testid="action-deny"
        >
          <XCircle className="h-4 w-4 mr-2" />
          Deny Application
        </Button>
      </div>
    );
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      let url = `${API}/admin/export/applications`;
      if (filter !== "all") {
        url += `?status=${filter}`;
      }
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = response.headers.get("Content-Disposition")?.split("filename=")[1] || "applications_export.csv";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        a.remove();
        toast.success("Applications exported successfully");
      } else {
        toast.error("Failed to export applications");
      }
    } catch (error) {
      toast.error("Failed to export applications");
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteApplication = async () => {
    if (!deleteAppDialog) return;
    
    setDeletingApp(true);
    try {
      const response = await fetch(
        `${API}/admin/applications/${deleteAppDialog.application_id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to delete application");
      }

      const data = await response.json();
      toast.success(data.message);
      setDeleteAppDialog(null);
      setDetailModalOpen(false);
      setSelectedApp(null);
      fetchApplications();
    } catch (error) {
      toast.error(error.message || "Failed to delete application");
    } finally {
      setDeletingApp(false);
    }
  };

  const handleClearTestData = async () => {
    setClearingData(true);
    try {
      const response = await fetch(
        `${API}/admin/applications/clear-test-data`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to clear test data");
      }

      const data = await response.json();
      toast.success(`Test data cleared: ${data.deleted.applications} applications, ${data.deleted.clients} clients, ${data.deleted.users} users`);
      setClearDataDialog(false);
      fetchApplications();
    } catch (error) {
      toast.error(error.message || "Failed to clear test data");
    } finally {
      setClearingData(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-[1200px] mx-auto animate-fade-in">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-rnb-navy tracking-tight">
              Program Applications
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Review and manage program applications
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setClearDataDialog(true)}
              className="text-xs gap-1.5 h-9 text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200"
              data-testid="clear-test-data-btn"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Clear Test
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exporting}
              className="text-xs gap-1.5 h-9 border-gray-200 text-gray-500 hover:text-rnb-navy"
              data-testid="export-applications-btn"
            >
              <Download className="h-3.5 w-3.5" />
              {exporting ? "Exporting..." : "Export"}
            </Button>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-44 h-9 text-xs rounded-xl border-gray-200" data-testid="filter-status">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Applications</SelectItem>
                <SelectItem value="pending_payment">Pending Payment</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="denied">Denied</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-rnb-blue/20 border-t-rnb-blue rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {filteredApplications.length > 0 ? (
              filteredApplications.map((app) => (
                <Card
                  key={app.application_id}
                  data-testid={`app-${app.application_id}`}
                  className="hover:shadow-md transition-all duration-200 border-gray-100 rounded-xl overflow-hidden"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="font-heading text-xl font-semibold text-rnb-navy">
                            {app.applicant_name}
                          </h3>
                          {getStatusBadge(app.status, app.payment_status)}
                          {app.submission_id && (
                            <Badge variant="outline" className="font-mono text-xs">
                              {app.submission_id}
                            </Badge>
                          )}
                        </div>

                        <div className="grid md:grid-cols-4 gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4 text-rnb-gray" />
                            <div>
                              <p className="text-rnb-gray text-xs">Company</p>
                              <p className="font-medium text-rnb-navy">{app.company_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Briefcase className="h-4 w-4 text-rnb-gray" />
                            <div>
                              <p className="text-rnb-gray text-xs">Program</p>
                              <p className="font-medium text-rnb-navy">
                                {programNames[app.program_type] || app.program_type}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-rnb-gray" />
                            <div>
                              <p className="text-rnb-gray text-xs">Price</p>
                              <p className="font-mono-numbers font-semibold text-rnb-navy">
                                {app.amount
                                  ? `$${Number(app.amount).toLocaleString()}`
                                  : programPrices[app.program_type] || "N/A"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-rnb-gray" />
                            <div>
                              <p className="text-rnb-gray text-xs">Applied</p>
                              <p className="font-medium text-rnb-navy">
                                {formatDate(app.created_at).split(",")[0]}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDetailModal(app)}
                          data-testid={`view-${app.application_id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Details
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteAppDialog(app)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-${app.application_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="h-12 w-12 text-rnb-gray/30 mx-auto mb-4" />
                  <p className="text-rnb-gray">
                    {filter === "all"
                      ? "No applications yet"
                      : `No ${filter.replace("_", " ")} applications`}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Application Detail Modal */}
        <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedApp && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-3">
                    <DialogTitle className="text-xl">
                      Application Details
                    </DialogTitle>
                    {getStatusBadge(selectedApp.status, selectedApp.payment_status)}
                  </div>
                  <DialogDescription>
                    {selectedApp.submission_id && (
                      <span className="font-mono text-sm">
                        Submission ID: {selectedApp.submission_id}
                      </span>
                    )}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                  {/* Applicant Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-rnb-gray text-sm">
                        <User className="h-4 w-4" />
                        Applicant Name
                      </div>
                      <p className="font-semibold text-rnb-navy">
                        {selectedApp.applicant_name}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-rnb-gray text-sm">
                        <Mail className="h-4 w-4" />
                        Email
                      </div>
                      <p className="font-medium text-rnb-navy">
                        {selectedApp.applicant_email}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-rnb-gray text-sm">
                        <Building className="h-4 w-4" />
                        Company
                      </div>
                      <p className="font-medium text-rnb-navy">
                        {selectedApp.company_name}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-rnb-gray text-sm">
                        <Briefcase className="h-4 w-4" />
                        Company Size
                      </div>
                      <p className="font-medium text-rnb-navy capitalize">
                        {selectedApp.company_size?.replace(/-/g, " ") || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Program Info */}
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-rnb-navy mb-3">Program Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-rnb-gray text-sm">Program</p>
                        <p className="font-semibold text-rnb-navy">
                          {programNames[selectedApp.program_type] || selectedApp.program_type}
                        </p>
                      </div>
                      <div>
                        <p className="text-rnb-gray text-sm">Price</p>
                        <p className="font-mono-numbers font-bold text-rnb-navy text-lg">
                          {selectedApp.amount
                            ? `$${Number(selectedApp.amount).toLocaleString()}`
                            : programPrices[selectedApp.program_type] || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-rnb-gray text-sm">Industry</p>
                        <p className="font-medium text-rnb-navy capitalize">
                          {selectedApp.industry?.replace(/-/g, " ") || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-rnb-gray text-sm">Applied On</p>
                        <p className="font-medium text-rnb-navy">
                          {formatDate(selectedApp.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Application Details */}
                  {(selectedApp.challenges || selectedApp.goals || selectedApp.timeline || selectedApp.additional_info) && (
                    <div className="space-y-4">
                      <h4 className="font-semibold text-rnb-navy">Application Responses</h4>
                      
                      {selectedApp.challenges && (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-rnb-gray text-sm">
                            <Target className="h-4 w-4" />
                            Current Challenges
                          </div>
                          <p className="text-rnb-navy bg-white p-3 rounded border">
                            {selectedApp.challenges}
                          </p>
                        </div>
                      )}

                      {selectedApp.goals && (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-rnb-gray text-sm">
                            <Target className="h-4 w-4" />
                            Goals
                          </div>
                          <p className="text-rnb-navy bg-white p-3 rounded border">
                            {selectedApp.goals}
                          </p>
                        </div>
                      )}

                      {selectedApp.timeline && (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-rnb-gray text-sm">
                            <Calendar className="h-4 w-4" />
                            Timeline
                          </div>
                          <p className="text-rnb-navy bg-white p-3 rounded border">
                            {selectedApp.timeline}
                          </p>
                        </div>
                      )}

                      {selectedApp.additional_info && (
                        <div className="space-y-1">
                          <div className="text-rnb-gray text-sm">Additional Information</div>
                          <p className="text-rnb-navy bg-white p-3 rounded border">
                            {selectedApp.additional_info}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Selected Add-ons */}
                  {selectedApp.selected_addons && selectedApp.selected_addons.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-rnb-navy">Selected Add-ons</h4>
                      <div className="space-y-2">
                        {selectedApp.selected_addons.map((addon, idx) => (
                          <div key={addon.name || addon.title || i} className="flex justify-between items-center bg-white p-3 rounded border">
                            <span className="text-rnb-navy">{addon.name}</span>
                            <span className="font-mono-numbers font-semibold">
                              ${addon.price?.toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Status History */}
                  {selectedApp.approved_at && (
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <p className="text-green-800 text-sm">
                        <strong>Approved:</strong> {formatDate(selectedApp.approved_at)}
                      </p>
                    </div>
                  )}

                  {selectedApp.denied_at && (
                    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                      <p className="text-red-800 text-sm">
                        <strong>Denied:</strong> {formatDate(selectedApp.denied_at)}
                      </p>
                    </div>
                  )}

                  {selectedApp.payment_reminder_sent_at && (
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      <p className="text-yellow-800 text-sm">
                        <strong>Payment reminder sent:</strong> {formatDate(selectedApp.payment_reminder_sent_at)}
                      </p>
                    </div>
                  )}
                </div>

                <DialogFooter className="flex-col gap-4 sm:flex-col">
                  {renderActionButtons(selectedApp)}
                  
                  {/* Delete Application Button */}
                  <div className="w-full pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setDeleteAppDialog(selectedApp)}
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      data-testid="delete-application-detail-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Application
                    </Button>
                  </div>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Application Confirmation Dialog */}
        <Dialog open={!!deleteAppDialog} onOpenChange={() => setDeleteAppDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Delete Application
              </DialogTitle>
              <DialogDescription>
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            
            {deleteAppDialog && (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 mb-2">
                    Are you sure you want to delete this application?
                    {deleteAppDialog.status === "approved" && (
                      <span className="block mt-2 font-semibold">
                        This will also delete the associated client account and user login.
                      </span>
                    )}
                  </p>
                </div>
                
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-rnb-gray">Application Details</p>
                  <p className="font-medium">{deleteAppDialog.applicant_name}</p>
                  <p className="text-sm text-rnb-gray">{deleteAppDialog.applicant_email}</p>
                  <p className="text-sm mt-1">
                    Program: {programNames[deleteAppDialog.program_type] || deleteAppDialog.program_type}
                  </p>
                  {deleteAppDialog.submission_id && (
                    <p className="text-xs font-mono mt-1">ID: {deleteAppDialog.submission_id}</p>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteAppDialog(null)}>
                Cancel
              </Button>
              <Button
                onClick={handleDeleteApplication}
                disabled={deletingApp}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-application"
              >
                {deletingApp ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Application
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Clear All Test Data Dialog */}
        <Dialog open={clearDataDialog} onOpenChange={setClearDataDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Clear All Program Applications
              </DialogTitle>
              <DialogDescription>
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 mb-2">
                  Are you sure you want to clear ALL program application data? This will permanently delete:
                </p>
                <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>All program applications ({applications.length} total)</li>
                  <li>All associated client accounts</li>
                  <li>All associated user logins</li>
                </ul>
              </div>
              
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This is typically used to clear test data before going live. 
                  Do not use this in production with real clients.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setClearDataDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleClearTestData}
                disabled={clearingData}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-clear-applications"
              >
                {clearingData ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear All Data
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminApplications;
